import React, { useEffect, useState, useCallback } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { FaUsers, FaSyncAlt, FaPlus, FaWhatsapp, FaPhone, FaMapMarkerAlt, FaExclamationTriangle, FaArrowLeft, FaBullhorn } from 'react-icons/fa';
import {
  TechShell, TechPageHeader, TechSection, TechInput, TechSelect, TechTextarea, TechButton, TechButtonGhost,
  StatStrip, StatTile, StatLabel, StatValue, StatSub, StatusPill, TechChip, Empty, Loading, ErrorBox,
  borderSoft, mono, accent,
} from '../../styles/techComponents';
import { listVecinos, createVecino, getVecino, addInteraction, notifyVecino } from '../../services/vecinosService';

const Grid = styled.div` display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:0.7rem; `;
const Card = styled.div` border:1px solid ${borderSoft};border-left:3px solid ${accent};border-radius:8px;padding:0.8rem 0.9rem;
  background:rgba(11,19,32,0.4);cursor:pointer;transition:.15s; &:hover{transform:translateY(-1px)}
  h5{margin:0 0 0.25rem;font-size:0.95rem} .m{font-family:${mono};font-size:0.72rem;color:var(--text-secondary)} `;
const Form = styled.div` display:grid;grid-template-columns:1.4fr 1fr 1fr 1fr auto;gap:0.5rem;align-items:end;
  @media(max-width:900px){grid-template-columns:1fr 1fr} `;
const Lbl = styled.label` font-family:${mono};font-size:0.64rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--text-secondary);display:block;margin-bottom:0.2rem; `;
const Line = styled.div` padding:0.5rem 0;border-bottom:1px solid ${borderSoft};font-size:0.85rem;
  .t{font-family:${mono};font-size:0.7rem;color:var(--text-secondary)} `;

export default function Vecinos() {
  const [vecinos, setVecinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ nombre: '', telefono: '', barrio: '', direccion: '' });
  const [sel, setSel] = useState(null); // detalle
  const [msg, setMsg] = useState(null);
  const [inter, setInter] = useState({ tipo: 'nota', nota: '' });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setVecinos(await listVecinos()); }
    catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 3000); };

  async function onAdd() {
    if (!form.nombre.trim()) return flash('Poné un nombre');
    try { const v = await createVecino(form); setVecinos((p) => [v, ...p]); setForm({ nombre: '', telefono: '', barrio: '', direccion: '' }); flash('Vecino cargado'); }
    catch (e) { flash(e?.response?.data?.error || 'Error'); }
  }
  async function openDetail(id) {
    try { setSel(await getVecino(id)); } catch (e) { flash('No se pudo abrir'); }
  }
  async function onAddInteraction() {
    if (!inter.nota.trim()) return flash('Escribí una nota');
    try {
      const nueva = await addInteraction(sel.vecino.id, { tipo: inter.tipo, nota: inter.nota.trim() });
      setSel((s) => ({ ...s, interacciones: [nueva, ...(s.interacciones || [])] }));
      setInter({ tipo: 'nota', nota: '' });
      flash('Interacción registrada');
    } catch (e) { flash(e?.response?.data?.error || 'Error'); }
  }
  async function onNotify(caseworkId) {
    try {
      const r = await notifyVecino(sel.vecino.id, caseworkId ? { casework_id: caseworkId } : {});
      flash('Mensaje generado');
      setSel((s) => ({ ...s, generated: r }));
    } catch (e) { flash(e?.response?.data?.error || 'Error'); }
  }

  if (loading) return <TechShell><Loading>Cargando vecinos…</Loading></TechShell>;
  if (error) return <TechShell><ErrorBox><FaExclamationTriangle /> {error}</ErrorBox></TechShell>;

  const totalResueltos = vecinos.reduce((a, v) => a + (v.resueltos || 0), 0);

  // --- Detalle ---
  if (sel) {
    const v = sel.vecino;
    return (
      <TechShell>
        <TechButtonGhost onClick={() => setSel(null)} style={{ marginBottom: '0.6rem' }}><FaArrowLeft /> Volver</TechButtonGhost>
        {msg && <StatusPill style={{ marginLeft: 8 }}>{msg}</StatusPill>}
        <TechPageHeader title={v.nombre} dossier="VECINO" subtitle={[v.barrio_nombre, v.direccion, v.telefono].filter(Boolean).join(' · ')} />
        <TechSection title="Reclamos del vecino" icon={<FaMapMarkerAlt />}>
          {(sel.reclamos || []).length === 0 && <Empty>Sin reclamos asociados.</Empty>}
          {(sel.reclamos || []).map((r) => (
            <Line key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{r.titulo} <TechChip>{r.estado}</TechChip></span>
              {r.estado === 'resuelto' && <TechButtonGhost onClick={() => onNotify(r.id)}><FaBullhorn /> Avisar</TechButtonGhost>}
            </Line>
          ))}
          <div style={{ marginTop: '0.6rem' }}>
            <TechButton onClick={() => onNotify(null)}><FaWhatsapp /> Generar mensaje de gestión</TechButton>
          </div>
          {sel.generated && (
            <div style={{ marginTop: '0.8rem' }}>
              <Lbl>Mensaje para WhatsApp</Lbl>
              <div style={{ border: `1px solid ${borderSoft}`, borderRadius: 8, padding: '0.7rem', fontSize: '0.86rem', whiteSpace: 'pre-wrap' }}>{sel.generated.mensaje}</div>
              {sel.generated.wa_link && <TechButton as="a" href={sel.generated.wa_link} target="_blank" rel="noreferrer" style={{ marginTop: 6 }}><FaWhatsapp /> Abrir en WhatsApp</TechButton>}
            </div>
          )}
        </TechSection>
        <TechSection title="Historial de interacciones" icon={<FaUsers />}>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '0.5rem', alignItems: 'end', marginBottom: '0.8rem' }}>
            <div>
              <Lbl>Tipo</Lbl>
              <TechSelect value={inter.tipo} onChange={(e) => setInter({ ...inter, tipo: e.target.value })}>
                <option value="nota">Nota</option>
                <option value="llamada">Llamada</option>
                <option value="visita">Visita</option>
                <option value="whatsapp">WhatsApp</option>
              </TechSelect>
            </div>
            <div>
              <Lbl>Detalle</Lbl>
              <TechTextarea rows={2} placeholder="Qué pasó en la interacción con el vecino" value={inter.nota} onChange={(e) => setInter({ ...inter, nota: e.target.value })} />
            </div>
            <TechButton onClick={onAddInteraction}><FaPlus /> Registrar</TechButton>
          </div>
          {(sel.interacciones || []).length === 0 && <Empty>Sin interacciones registradas.</Empty>}
          {(sel.interacciones || []).map((i) => (
            <Line key={i.id}><span className="t">[{i.tipo}] {new Date(i.created_at).toLocaleString('es-AR')}</span><div>{i.nota}</div></Line>
          ))}
        </TechSection>
      </TechShell>
    );
  }

  // --- Lista ---
  return (
    <TechShell>
      <TechPageHeader
        title="Vecinos"
        dossier="CRM · TERRITORIO"
        subtitle="La persona detrás de cada reclamo. Cargá vecinos, seguí su gestión y avisales cuando se resuelve: gestión que se convierte en voto fiel."
        actions={<TechButtonGhost onClick={load}><FaSyncAlt /> Actualizar</TechButtonGhost>}
      />
      <StatStrip>
        <StatTile><StatLabel>Vecinos</StatLabel><StatValue>{vecinos.length}</StatValue><StatSub>en la base</StatSub></StatTile>
        <StatTile><StatLabel>Con teléfono</StatLabel><StatValue>{vecinos.filter((v) => v.telefono).length}</StatValue><StatSub>contactables por WhatsApp</StatSub></StatTile>
        <StatTile><StatLabel>Gestiones resueltas</StatLabel><StatValue>{totalResueltos}</StatValue><StatSub>para avisar y fidelizar</StatSub></StatTile>
      </StatStrip>
      {msg && <StatusPill style={{ marginBottom: '0.5rem' }}>{msg}</StatusPill>}

      <TechSection title="Cargar vecino" icon={<FaPlus />}>
        <Form>
          <div><Lbl>Nombre</Lbl><TechInput placeholder="Nombre y apellido" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></div>
          <div><Lbl>Teléfono</Lbl><TechInput placeholder="Ej: 3364..." value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
          <div><Lbl>Barrio</Lbl><TechInput placeholder="Barrio" value={form.barrio} onChange={(e) => setForm({ ...form, barrio: e.target.value })} /></div>
          <div><Lbl>Dirección</Lbl><TechInput placeholder="Calle y número" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} /></div>
          <TechButton onClick={onAdd}><FaPlus /> Cargar</TechButton>
        </Form>
      </TechSection>

      <TechSection title={`Vecinos (${vecinos.length})`} icon={<FaUsers />}>
        {vecinos.length === 0 && <Empty>Sin vecinos cargados. Empezá a cargar los contactos de tus recorridas.</Empty>}
        <Grid>
          {vecinos.map((v) => (
            <Card key={v.id} onClick={() => openDetail(v.id)}>
              <h5>{v.nombre}</h5>
              <div className="m">{[v.barrio_nombre, v.direccion].filter(Boolean).join(' · ') || '—'}</div>
              <div className="m">{v.telefono ? <><FaPhone /> {v.telefono}</> : 'sin teléfono'}</div>
              <div style={{ marginTop: 6, display: 'flex', gap: 4 }}>
                <TechChip>{v.reclamos || 0} reclamos</TechChip>
                {v.resueltos > 0 && <TechChip>{v.resueltos} resueltos</TechChip>}
              </div>
            </Card>
          ))}
        </Grid>
      </TechSection>
    </TechShell>
  );
}
